import { ArrowLeftOutlined } from '@ant-design/icons';
import { templateAtVersion } from '@bmo/form-core';
import { Alert, Button, Spin, Tag, Typography } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { type WorkflowTemplate, type WorkflowVersion, workflowsApi } from '@/api/workflows';
import { ClientBadge } from '@/components/ClientBadge';
import { type FieldValue } from '@/form-renderer/FieldRenderer';
import { FormShell } from '@/form-renderer/FormShell';
import { followOptionRedirect } from '@/form-renderer/optionRedirect';
import { useFormBranch } from '@/form-renderer/useFormBranch';
import { handleApiError } from '@/lib/handleApiError';

import { DevicePreviewFrame, DeviceViewportSwitcher, type DeviceKind } from './DevicePreviewFrame';

/**
 * Optional sample-values fixture for the previewed workflow. Lives under
 * `public/sample-values.json` next to the dev server — the seed script writes
 * this file when it runs so the originator can hit Preview and immediately
 * see the Application Review with realistic data, instead of typing every
 * field by hand. Production builds simply don't ship this file (404 → null).
 */
type SampleValuesFixture = {
  templateId: string;
  values: Record<string, FieldValue>;
};

const { Title, Text } = Typography;

/**
 * Flex-basis floor for the preview header's title block, in px.
 *
 * Sized just under the device switcher + Back button's own intrinsic width
 * (~310px), so the two only share a row when BOTH fit comfortably: the moment
 * the viewport can't hold them side by side, the controls wrap to a second row
 * instead of eating into the title. A smaller value would let the title keep
 * shrinking past readability on a phone; a larger one would wrap the controls
 * on a tablet that has room for both.
 */
const TITLE_MIN_WIDTH = 320;

/**
 * Originator-side preview of a workflow's DynamicForm steps. Uses the same
 * <FormShell> + <FormRenderer> that the eventual Advisor route will use, so
 * what the originator sees in Preview is exactly what the advisor will see
 * once the Advisor experience ships.
 *
 * No API actions — values live in component state and are lost on refresh
 * (intentional: Preview is for shape-checking, not data persistence).
 * Navigation between steps is free (mode='preview'); the rail shows a blue
 * check on steps whose required fields are all filled.
 */
export function PreviewPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id: templateId } = useParams<{ id: string }>();
  const { pathname } = useLocation();
  // Optional ?stepId=… so the Approver review can jump straight to a specific
  // step's preview. Falls back to the workflow's first step.
  // Optional ?version=N pins the preview to a specific (archived or pending)
  // version's frozen step list — without it the preview reflects the
  // template's CURRENT state, which would leak v2's edits into v1's
  // preview after a new version is published.
  const [searchParams] = useSearchParams();
  const initialStepId = searchParams.get('stepId') ?? undefined;
  const versionParam = searchParams.get('version');
  const requestedVersion = versionParam !== null ? Number(versionParam) : null;
  const [template, setTemplate] = useState<WorkflowTemplate | null>(null);
  const [pinnedVersion, setPinnedVersion] = useState<WorkflowVersion | null>(null);
  const [sampleValues, setSampleValues] = useState<Record<string, FieldValue> | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(true);
  // Set when a specific ?version=N was requested but can't be resolved to a
  // frozen snapshot (unknown number, or a non-numeric param). We surface an
  // explicit error instead of silently rendering the template's CURRENT state,
  // which would masquerade as the requested version.
  const [versionNotFound, setVersionNotFound] = useState(false);
  // Device form-factor for the preview frame. <FormShell> reflows itself once
  // the frame gets narrow (rail collapses), so this only has to drive width.
  const [device, setDevice] = useState<DeviceKind>('desktop');
  const language = i18n.language.startsWith('fr') ? 'fr' : 'en';
  // The "CLIENT:" badge, reported by the shell — same as the advisor page.
  const [clientName, setClientName] = useState<string | null>(null);
  // `t` behind a ref so the load effect below can report an error in the
  // CURRENT language without taking `t` as a dependency. `useTranslation`
  // mints a new `t` on every locale change, so depending on it made a header
  // language switch re-run the fetch — which flipped `loading` back on, tore
  // <FormShell> down, and remounted it with the new locale as its
  // `initialLanguage`. That reset the Welcome step's language radio (and every
  // answer typed into the preview) on a switch the advisor runtime leaves
  // alone, because AdvisorApplicationPage's load effect never depended on `t`.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => {
    if (!templateId) return;
    let cancelled = false;
    setLoading(true);
    setVersionNotFound(false);
    (async () => {
      try {
        // A specific version was requested via ?version=N. A non-numeric param
        // (Number(...) → NaN) can never match a row, so treat it as not-found
        // without even hitting the versions endpoint.
        const versionRequested = requestedVersion !== null;
        const versionResolvable = versionRequested && Number.isFinite(requestedVersion);
        // Fetch the workflow + the optional sample-values fixture in parallel.
        // The fixture is just a static JSON file the seed script drops into
        // `public/`, so a missing/stale file is non-fatal: we silently ignore.
        // When a specific version is requested, also pull the versions list
        // so we can swap its frozen step list onto the template object before
        // handing it to <FormShell>. Everything else (name / description /
        // type) stays on the template — only the steps shape needs the
        // version-frozen snapshot.
        // The pinned version is fetched directly rather than picked out of a list: the
        // versions list no longer carries step snapshots, and the snapshot is the only
        // reason this page wants a version at all. A miss resolves to null, which is
        // already the not-found path below.
        const [fresh, fixtureValues, pinned] = await Promise.all([
          workflowsApi.get(templateId),
          fetchSampleValues(templateId),
          versionResolvable
            ? workflowsApi.getVersion(templateId, requestedVersion).catch(() => null)
            : Promise.resolve<WorkflowVersion | null>(null),
        ]);
        if (cancelled) return;
        if (versionRequested) {
          // Requested a version we can't resolve — show the error rather than
          // falling back to the workflow's current state.
          if (!pinned) {
            setVersionNotFound(true);
            return;
          }
          setTemplate(templateAtVersion(fresh, pinned));
          setPinnedVersion(pinned);
        } else {
          setTemplate(fresh);
          setPinnedVersion(null);
        }
        setSampleValues(fixtureValues ?? undefined);
      } catch (err) {
        if (!cancelled) {
          const translate = tRef.current;
          handleApiError(err, translate, translate('preview.loadFailed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `t` is read through `tRef` on purpose — see the ref's declaration. Adding it here
    // would refetch the workflow on every header language switch and remount <FormShell>.
  }, [templateId, requestedVersion]);

  // Form branching, exactly as the advisor runtime has it — a radio answer replaces every
  // step below the one carrying the rule. Preview is where an originator naturally checks a
  // rule they just authored, so the shape they see here has to be the shape it runs in.
  //
  // `workflowsApi.get` resolves the target to whatever the caller is entitled to: the
  // working copy for an originator, the published snapshot for an advisor. That matches how
  // this page already treats the BASE form, so the two halves of a branched preview stay
  // consistent with each other.
  const {
    effectiveTemplate,
    formBranch,
    onFormBranchChange,
    loading: branchLoading,
    loadFailed: branchLoadFailed,
  } = useFormBranch({ template, loadTemplate: workflowsApi.get });

  // Resolve which preview badge to show — Approved-not-yet-published /
  // Published / Rejected / Pending / Draft. Runs on every render but is
  // cheap; kept memoised so the render below stays declarative and the
  // early returns below can still short-circuit safely.
  const previewState = useMemo(
    () => resolvePreviewState(pinnedVersion, template),
    [pinnedVersion, template],
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }
  if (versionNotFound) {
    return (
      <div style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <Text type="secondary">{t('preview.versionNotFound')}</Text>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          {t('preview.back')}
        </Button>
      </div>
    );
  }
  if (!template) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="secondary">{t('preview.notFound')}</Text>
      </div>
    );
  }

  const workflowTitle = language === 'fr' && template.nameFr ? template.nameFr : template.nameEn;

  return (
    <div
      style={{
        // Horizontal padding lives on <AppShell> (24 vertical / 32 horizontal).
        // Adding more here would double-pad the page and make the form's
        // left / right margins feel bigger than every other route.
        paddingBottom: 24,
        background: '#f6f7fb',
        minHeight: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Title block and the device/Back controls share one row on a wide screen and
          peel apart as the page narrows: title → switcher → Back, up to three rows.

          Both halves have to be able to give, or the next one along is clipped:

            • the ROW wraps and the title block carries a flex-basis floor
              (TITLE_MIN_WIDTH) — without both, the controls keep their intrinsic
              width, the title is the only item that can shrink, and at phone width
              it collapses to a one-character column of stacked letters;

            • the CONTROLS block then wraps internally and is left shrinkable
              (`0 1 auto` + `minWidth: 0`) — the switcher and Back together are
              ~320px, which is wider than a 375px screen's content box once the
              page padding and a scrollbar are taken out, so pinned at their
              intrinsic width they ran Back off the right edge instead of
              dropping it to a line of its own.

          Self-tuning rather than pinned to a breakpoint: each piece moves down at
          the width where it genuinely stops fitting, so a longer "Back" label in
          French — or another control added here later — needs no new number. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            minWidth: 0,
            flexWrap: 'wrap',
            flex: `1 1 ${TITLE_MIN_WIDTH}px`,
          }}
        >
          {/* The page's <h1> (WCAG 1.3.1 / 2.4.6). Step headings inside the
              form render at h3/h4 and sit correctly beneath it. */}
          <Title level={1} style={{ margin: 0, fontSize: 24, lineHeight: 1.35, fontWeight: 600 }}>
            {workflowTitle}
          </Title>
          <ClientBadge name={clientName} />
          {previewState && (
            <Tag
              color={previewState.color}
              style={{
                marginInlineEnd: 0,
                fontWeight: 600,
                letterSpacing: 0.2,
              }}
            >
              {t(previewState.labelKey, { defaultValue: previewState.fallback })}
            </Tag>
          )}
          <Text type="secondary" style={{ fontSize: 13 }}>
            {t('preview.modeBadge')}
          </Text>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            // No GROW (the block keeps its intrinsic width on a wide page, so
            // Back stays at the right edge under `space-between`) but shrink is
            // allowed, and `minWidth: 0` lets it shrink past its content —
            // which is what lets the wrap below actually fire.
            flex: '0 1 auto',
            minWidth: 0,
            flexWrap: 'wrap',
          }}
        >
          <DeviceViewportSwitcher device={device} onChange={setDevice} />
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            {t('preview.back')}
          </Button>
        </div>
      </div>
      {/* Branch notices sit OUTSIDE the device frame: they are messages to the person
          previewing, not part of the form being previewed, and rendering them inside would
          make a mobile viewport's narrow column carry page chrome. */}
      {branchLoading && (
        <Alert
          type="info"
          showIcon
          icon={<Spin size="small" />}
          style={{ marginBottom: 12 }}
          message={t('preview.branch.loading')}
        />
      )}
      {branchLoadFailed && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('preview.branch.unavailable')}
        />
      )}
      {/* A pinned version freezes the BASE form, but nothing records which version of the
          TARGET it was authored against — so the branched half resolves live. Said out loud
          rather than left to be discovered: the whole point of `?version=N` is that what you
          are looking at is a frozen artifact, and here half of it is not. */}
      {pinnedVersion && formBranch && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('preview.branch.notVersionPinned')}
        />
      )}
      <DevicePreviewFrame device={device}>
        <FormShell
          // The COMPOSED template once a branch is active — and never a different `key`. A
          // remount would discard every answer, including the one that selected this form,
          // which would immediately un-select it again.
          template={effectiveTemplate ?? template}
          formBranch={formBranch}
          // No `payloadTemplate`: preview never saves or submits, so there is no snapshot
          // for it to scope. Passing one would imply a write path that does not exist.
          onFormBranchChange={onFormBranchChange}
          language={language}
          mode="preview"
          initialStepId={initialStepId}
          onPrimaryClientNameChange={setClientName}
          initialValues={sampleValues}
          // No `onSaveAndExit`: the button is parked for now and must not render on either
          // preview surface. Absence is the gate the shell already uses for the public
          // customer — pass the handler back (`() => navigate(-1)`) to restore the button.
          // A redirect stays on the surface it fired from: this same component
          // serves the Originator AND the Approver preview route, and sending an
          // Approver to /originator/… would bounce them off a role gate for
          // simply exercising the form they are reviewing.
          onOptionRedirect={(target) =>
            followOptionRedirect(target, navigate, (id) => {
              const base = pathname.startsWith('/approver/') ? '/approver' : '/originator';
              return `${base}/workflows/${id}/preview`;
            })
          }
        />
      </DevicePreviewFrame>
    </div>
  );
}

/**
 * Compute the state chip shown next to the workflow title on the preview
 * header. Only fires when a specific `?version=N` is pinned — without a
 * version param the preview shows the template's live state and the chip
 * is suppressed (the small "no data is saved" note still shows).
 *
 * Rules (highest precedence first), matching the same Approved+!active split
 * used everywhere else in the app (see `StatusTag`'s `hasPendingApproval` and
 * `isCurrentVersion` docs, and `VersionDetailsPage`'s `isPendingPublish`) —
 * version-scoped, so "current but not live" reads as "Unpublished", not the
 * template-scoped "Pending Publishing":
 *
 *   • Approved + isActive === true → "Published (live)"
 *
 *   • Approved + template.pendingApprovedVersionNumber === this version →
 *     "Pending Publishing" — a re-approval staged on top of an already-live
 *     (or already-current) predecessor. The domain deliberately leaves
 *     `currentVersionNumber` pointing at that predecessor while this version
 *     waits for the Approver's promote toggle, so it must be checked BEFORE
 *     the `isCurrent` comparison below — otherwise it reads as "Approved
 *     (archived)", the same as a version genuinely superseded by a newer one.
 *
 *   • Approved + template.currentVersionNumber === this version +
 *     template.isActive === false → "Unpublished" — covers both a
 *     freshly-approved version awaiting its first publish and a
 *     previously-live version the Approver has since taken offline.
 *
 *   • Approved + !current → "Approved (archived)" — an older approved
 *     version that's since been superseded.
 *
 *   • Rejected → "Rejected"
 *   • Pending  → "Pending review"
 *   • Draft    → "Draft"
 *
 * Returns null when there's no pinned version — the caller renders no
 * chip in that case so the current-template preview stays unbadged.
 */
type PreviewStateChip = {
  labelKey: string;
  fallback: string;
  color: string;
};
function resolvePreviewState(
  version: WorkflowVersion | null,
  template: WorkflowTemplate | null,
): PreviewStateChip | null {
  if (!version || !template) return null;
  const isCurrent = template.currentVersionNumber === version.versionNumber;
  const isLive = isCurrent && template.isActive;
  const isPendingPublish = template.pendingApprovedVersionNumber === version.versionNumber;
  switch (version.status) {
    case 'Approved': {
      if (isLive) {
        return {
          labelKey: 'preview.state.publishedLive',
          fallback: 'Published (live)',
          color: 'success',
        };
      }
      if (isPendingPublish) {
        return {
          labelKey: 'workflow.statusLabels.PendingPublishing',
          fallback: 'Pending Publishing',
          color: 'cyan',
        };
      }
      if (isCurrent) {
        return {
          labelKey: 'workflow.versions.statusUnpublished',
          fallback: 'Unpublished',
          color: 'orange',
        };
      }
      return {
        labelKey: 'preview.state.approvedArchived',
        fallback: 'Approved (archived)',
        color: 'default',
      };
    }
    case 'Rejected':
      return {
        labelKey: 'preview.state.rejected',
        fallback: 'Rejected',
        color: 'error',
      };
    case 'Pending':
      return {
        labelKey: 'preview.state.pending',
        fallback: 'Pending review',
        color: 'processing',
      };
    case 'Draft':
      return {
        labelKey: 'preview.state.draft',
        fallback: 'Draft',
        color: 'default',
      };
    default:
      return null;
  }
}

/**
 * Try to fetch `public/sample-values.json`. Returns null on any failure
 * (file missing, network error, malformed JSON, or the fixture is for a
 * different workflow). Production builds simply don't ship the file.
 */
async function fetchSampleValues(templateId: string): Promise<Record<string, FieldValue> | null> {
  try {
    const res = await fetch('/sample-values.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const parsed = (await res.json()) as SampleValuesFixture;
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.templateId !== templateId) return null;
    return parsed.values ?? null;
  } catch {
    return null;
  }
}
